/**
 * One key per checkout attempt, and "did it go through?" before any error.
 *
 * Bug Intake #637 (https://trello.com/c/ZEUtXAVG): a Place Order whose
 * `/checkout` response never arrived had still placed the order. The page
 * showed an error, the shopper pressed Place Order again, and a second order
 * was built; both cards were charged at release.
 *
 * TWO HALVES, both here so StorefrontCart only wires them:
 *
 *  1. THE ATTEMPT KEY. Sent as `checkout_attempt_key` with create_pending_order
 *     and checkout. The server records it on the order it builds and answers a
 *     repeated key with THAT order (api/src/services/checkout_attempt.py). So
 *     the browser must REUSE the key while the attempt is unsettled -- a new
 *     key per click is a new order per click -- and ROTATE it once the attempt
 *     is settled (placed, or definitively refused), or a later, different
 *     order would be answered with this one. It is kept in sessionStorage per
 *     account and store, so a reload mid-attempt keeps it, alongside a hash of
 *     what is being ordered: a changed cart or address is a new attempt.
 *     Nothing but the key and the hash is stored.
 *
 *  2. THE CONFIRMATION. On an AMBIGUOUS failure (no response, or a 5xx: the
 *     order may exist) the page asks `checkout_attempt_status` before saying
 *     anything. A 4xx is a definitive refusal and is shown as today.
 */
import { SUPPRESS_ERROR_TOAST } from "@/interceptors/authResponseInterceptor";

const STORAGE_PREFIX = "tf-checkout-attempt";

/** Status-check waits (ms) before each ask: about 10 s in all. */
export const CONFIRM_DELAYS_MS = [0, 1500, 3000, 6000];

/** A fresh opaque key in the server's accepted shape ([A-Za-z0-9_-]{16,64}). */
export function newAttemptKey(cryptoImpl = globalThis.crypto) {
  if (cryptoImpl && typeof cryptoImpl.randomUUID === "function") {
    return `att_${cryptoImpl.randomUUID().replace(/-/g, "")}`;
  }
  const bytes = new Uint8Array(16);
  if (cryptoImpl && typeof cryptoImpl.getRandomValues === "function") {
    cryptoImpl.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return `att_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const keys = Object.keys(value)
    .filter((k) => value[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
}

/**
 * A short hash of what the attempt orders (FNV-1a, 32-bit). Not a security
 * control -- the server never sees it -- only "is this still the same
 * attempt?", and it keeps the shopper's address out of storage.
 */
export function attemptFingerprint(value) {
  const text = stableStringify(value);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

function slot(accountId, storeId) {
  return `${STORAGE_PREFIX}:${accountId}:${storeId}`;
}

function readSlot(storage, name) {
  try {
    const raw = storage && storage.getItem(name);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed.key === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function writeSlot(storage, name, value) {
  try {
    if (storage) storage.setItem(name, JSON.stringify(value));
  } catch {
    // Private mode / quota: the key still protects this page's retries.
  }
}

/**
 * The key for the attempt identified by `fingerprint`: the stored one while
 * the attempt is unchanged, else a fresh one (stored).
 */
export function attemptKeyFor({ storage, accountId, storeId, fingerprint, generate = newAttemptKey }) {
  const name = slot(accountId, storeId);
  const stored = readSlot(storage, name);
  if (stored && stored.fp === fingerprint) return stored.key;
  const key = generate();
  writeSlot(storage, name, { key, fp: fingerprint });
  return key;
}

/** Settle the attempt: the next Place Order is a new one. */
export function clearAttemptKey({ storage, accountId, storeId }) {
  try {
    if (storage) storage.removeItem(slot(accountId, storeId));
  } catch {
    // Nothing stored, or storage unavailable: nothing to clear.
  }
}

function isCancellation(err) {
  return Boolean(
    err &&
      (err.name === "CanceledError" || err.name === "AbortError" || err.code === "ERR_CANCELED")
  );
}

/**
 * Could the request have done its work anyway? True for no response at all
 * (network error, timeout, dropped connection) and for a 5xx; false for a
 * 4xx, which is a definitive refusal, and for a cancellation.
 */
export function isAmbiguousFailure(err) {
  if (!err || isCancellation(err)) return false;
  const status = err.response && err.response.status;
  if (!status) return true;
  return status >= 500;
}

function defaultSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Ask the server whether attempt `key` placed an order.
 *
 * Resolves to `{ outcome, result }`:
 *   - `placed`  -- it went through (`result` names the order);
 *   - `pending` -- the order exists but is not finished; re-send the same
 *     request (it is idempotent) to finish it;
 *   - `none`    -- no order exists (404, or the attempt was canceled);
 *   - `unknown` -- the server could not be reached. NEVER read as "none":
 *     only the server can say no order exists.
 */
export async function confirmCheckoutAttempt({
  post,
  accountId,
  storeId,
  key,
  delaysMs = CONFIRM_DELAYS_MS,
  sleep = defaultSleep,
}) {
  for (const delay of delaysMs) {
    if (delay > 0) await sleep(delay);
    let response;
    try {
      response = await post(
        `/accounts/${accountId}/checkout_attempt_status`,
        {
          meta: {
            method: "checkout_attempt_status",
            args: { checkout_attempt_key: key, store_id: storeId },
          },
        },
        { [SUPPRESS_ERROR_TOAST]: true }
      );
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 404) return { outcome: "none", result: null };
      if (status && status < 500) return { outcome: "unknown", result: null };
      continue;
    }
    const result = response && response.data && response.data.meta && response.data.meta.result;
    const state = result && result.state;
    if (state === "placed" || state === "pending") return { outcome: state, result };
    if (state === "canceled") return { outcome: "none", result };
  }
  return { outcome: "unknown", result: null };
}
